import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import { getKongLocationAll } from 'features/Location/services/location'
import type { GetKongLocationAllResponseType } from 'features/Location/@types/location'
import {
  postImage,
  type PostImageResponseType,
  type PostImageRequestPayload,
} from 'utils/apiList/images'
import { toastSuccess } from 'utils/toast'
import {
  getIncentiveList,
  getIncentiveDetail,
  postAddEditIncentive,
  type IncentiveItemType,
  type PostAddEditIncentiveRequestType,
} from '../../services/incentive'

export const SLICE_NAME = 'masterIncentive'

export type IncentiveDetailDataType = IncentiveItemType & {
  location_list: {
    location_id: number
    location_name: string
  }[]
}

export const actFetchIncentiveDataList = createAsyncThunk(
  `${SLICE_NAME}/actFetchIncentiveDataList`,
  async (_, { getState }) => {
    const {
      masterIncentive: { queryParams },
    } = getState() as StoreStateType

    const response = await getIncentiveList({
      ...(queryParams.cursor && { cursor: queryParams.cursor }),
      name: queryParams.search || undefined,
      activity_type: queryParams.activity || undefined,
      incentive_type: queryParams.type || undefined,
      status: queryParams.status || undefined,
      page_size: queryParams.page_size,
    })

    return response.data
  },
)

export const actGetLocationById = createAsyncThunk(
  `${SLICE_NAME}/actGetLocationById`,
  async (id: number, { rejectWithValue }) => {
    try {
      const { data } = await getKongLocationAll({ params: { id, page: 0, limit: 1 } })
      return data.data.location[0]
        ? {
            location_id: data.data.location[0].location_id,
            location_name: data.data.location[0].location_name,
          }
        : null
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)
export const actFetchLocationList = createAsyncThunk<
  GetKongLocationAllResponseType['location'],
  void
>(`${SLICE_NAME}/actFetchLocationList`, async (_, { getState, rejectWithValue }) => {
  try {
    const {
      masterIncentive: { queryParamsForLocationList },
    } = getState() as StoreStateType
    const { type, ...rest } = queryParamsForLocationList
    const paramsToSubmit = {
      ...rest,
      ...(type !== 'all' && { type }),
    }
    const { data } = await getKongLocationAll({ params: paramsToSubmit })
    return data.data.location
  } catch (error) {
    callErrorMsg(error)
    return rejectWithValue(error)
  }
})

export const actFetchIncentiveDetail = createAsyncThunk<IncentiveDetailDataType, number>(
  `${SLICE_NAME}/actFetchIncentiveDetail`,
  async (id, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getIncentiveDetail(id)
      const responseLocations = await Promise.all(
        data.data.location_ids.map((locationId) =>
          dispatch(actGetLocationById(locationId)).unwrap(),
        ),
      )
      return {
        ...data.data,
        location_list: responseLocations.filter(
          (loc): loc is NonNullable<typeof loc> => loc !== null,
        ),
      }
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

type ActPostImageIncentiveArgType = {
  payload: PostImageRequestPayload
  callbackFunction: (filePath: string) => void
}
export const actPostImageIncentive = createAsyncThunk<
  PostImageResponseType['data'],
  ActPostImageIncentiveArgType
>(`${SLICE_NAME}/actPostImageIncentive`, async (params, { rejectWithValue }) => {
  const { payload, callbackFunction } = params
  const { file_input, created_by } = payload
  try {
    const { data } = await postImage('order-incentive-setting-image', {
      file_input,
      created_by,
    })
    callbackFunction(
      data.data.images.find((item) => item.type === 'real' && item.filePath)?.filePath || '',
    )
    return data.data
  } catch (error) {
    callErrorMsg(error)
    return rejectWithValue(error)
  }
})

type ActSubmitIncentiveDataArgType = {
  payload: PostAddEditIncentiveRequestType
  callbackFunction: () => void
}

export const actSubmitIncentiveData = createAsyncThunk<void, ActSubmitIncentiveDataArgType>(
  `${SLICE_NAME}/actSubmitIncentiveData`,
  async ({ payload, callbackFunction }, { rejectWithValue }) => {
    try {
      const { data } = await postAddEditIncentive(payload)
      toastSuccess(data.message)
      callbackFunction()
      return undefined
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)
